import React, { useEffect } from "react";

import type { TapIn } from "apps/user/learner/lib/tap-in";
import { useTapIn } from "apps/user/learner/lib/use-tap-in";
import { useTranslation } from "lib/i18n/use-translation";
import { trackEvent } from "utility/mixpanel";
import { useInViews } from "utility/useInViews";

import OriAvatar from "./OriAvatar";
import styles from "./AskOriPill.module.scss";

const Pill: React.FC<{ tapIn: TapIn }> = ({ tapIn }) => {
  const { open } = useTapIn();
  const { t } = useTranslation(["components"]);
  const { ref, inView } = useInViews();

  const analytics = {
    "Entry Point": tapIn.entryPoint,
    "Tap-In Surface": tapIn.context.surface,
    "Domain Key": tapIn.context.domain_key ?? null,
  };

  useEffect(() => {
    if (inView) trackEvent("Ori Tap-In Impression", analytics);
    // `analytics` derives from `tapIn`; its entry point and domain are the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, tapIn.entryPoint, tapIn.context.domain_key]);

  const handleClick = () => {
    const queued = open(tapIn);
    trackEvent("Ori Tap-In Tapped", { ...analytics, "Message Queued": queued });
  };

  return (
    <button
      ref={ref}
      type="button"
      className={styles.pill}
      onClick={handleClick}
      data-testid={`ask-ori-${tapIn.entryPoint}`}
    >
      {/* The avatar labels itself "Ori"; the button's name is the label alone. */}
      <span aria-hidden="true">
        <OriAvatar size={20} />
      </span>
      {t(tapIn.labelKey)}
    </button>
  );
};

interface AskOriPillProps {
  /** Built only once the flags are on, so a flag-off screen runs none of it. */
  getTapIn: () => TapIn;
}

/**
 * The shared web Ori entry point for a screen. Renders nothing unless the
 * company's Ori entitlement and `ori_tap_in` are both on, so a placement needs
 * no gate of its own.
 */
const AskOriPill: React.FC<AskOriPillProps> = ({ getTapIn }) => {
  const { enabled } = useTapIn();
  return enabled ? <Pill tapIn={getTapIn()} /> : null;
};

export default AskOriPill;
